import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import StaffNavbar from '../components/StaffNavbar';
import BackButton from '../components/BackButton';
import apiService from '../services/api';
import '../styles/StaffPages.css';

const STATUS_COLORS = {
  running: '#2563eb',
  success: '#10b981',
  partial: '#f59e0b',
  failed: '#ef4444',
  abandoned: '#6b7280',
  downloaded: '#10b981',
  downloading: '#2563eb',
  pending: '#6b7280',
  skipped: '#6b7280',
};

const formatDateTime = (value) => {
  if (!value) return '—';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? '—' : date.toLocaleString();
};

const formatBytes = (value) => {
  if (value === null || value === undefined) return '—';
  let size = Number(value);
  if (Number.isNaN(size)) return '—';
  const units = ['B', 'KB', 'MB', 'GB'];
  let unit = 0;
  while (size >= 1024 && unit < units.length - 1) {
    size /= 1024;
    unit += 1;
  }
  return unit === 0 ? `${size} B` : `${size.toFixed(1)} ${units[unit]}`;
};

const formatDuration = (ms) => (ms === null || ms === undefined ? '—' : `${(ms / 1000).toFixed(1)}s`);

const cardStyle = {
  backgroundColor: 'white',
  borderRadius: '12px',
  border: '1px solid #e5e7eb',
  padding: '20px',
  marginBottom: '20px',
};

const thStyle = {
  textAlign: 'left',
  padding: '10px 12px',
  fontFamily: '"Baikal Book"',
  fontSize: '12px',
  color: '#666',
  borderBottom: '1px solid #e5e7eb',
  whiteSpace: 'nowrap',
};

const tdStyle = {
  padding: '10px 12px',
  fontFamily: '"Baikal Book"',
  fontSize: '13px',
  borderBottom: '1px solid #f1f3f5',
};

const StatusPill = ({ status, label }) => (
  <span style={{
    color: STATUS_COLORS[status] || '#111',
    fontWeight: 600,
    fontFamily: '"Baikal Book"',
    fontSize: '13px',
  }}>
    {label || status}
  </span>
);

const Banner = ({ kind, children }) => {
  const palette = kind === 'error'
    ? { bg: '#fee2e2', border: '#fecaca', text: '#991b1b' }
    : kind === 'warning'
      ? { bg: '#fef3c7', border: '#fde68a', text: '#92400e' }
      : { bg: '#dbeafe', border: '#bfdbfe', text: '#1e40af' };
  return (
    <div style={{
      padding: '12px 16px',
      backgroundColor: palette.bg,
      border: `1px solid ${palette.border}`,
      borderRadius: '8px',
      color: palette.text,
      fontFamily: '"Baikal Book"',
      fontSize: '14px',
      marginBottom: '16px',
    }}>
      {children}
    </div>
  );
};

const SuperuserAccessPulls = () => {
  const navigate = useNavigate();

  const [status, setStatus] = useState(null);
  const [runs, setRuns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);

  const [selectedRun, setSelectedRun] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmPhrase, setConfirmPhrase] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const loadStatusAndRuns = useCallback(async () => {
    const [statusRes, runsRes] = await Promise.all([
      apiService.getAccessSftpStatus(),
      apiService.getAccessSftpRuns({ page_size: 25 }),
    ]);
    setStatus(statusRes);
    setRuns(Array.isArray(runsRes?.results) ? runsRes.results : []);
  }, []);

  useEffect(() => {
    const load = async () => {
      try {
        const currentUser = await apiService.fetchCurrentUser();
        if (!currentUser?.is_superuser) {
          navigate('/staff/dashboard/');
          return;
        }
        await loadStatusAndRuns();
      } catch (err) {
        console.error('Failed to load Access pulls:', err);
        setError('Failed to load Access pull history.');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [navigate, loadStatusAndRuns]);

  const refresh = async () => {
    try {
      await loadStatusAndRuns();
      setError(null);
    } catch (err) {
      console.error('Refresh failed:', err);
      setError('Could not refresh.');
    }
  };

  const openRun = async (runId) => {
    try {
      setSelectedFile(null);
      setSelectedRun(await apiService.getAccessSftpRun(runId));
    } catch (err) {
      console.error('Failed to load run:', err);
      setError('Could not load that run.');
    }
  };

  const openFile = async (fileId) => {
    try {
      setSelectedFile(await apiService.getAccessSftpFile(fileId));
    } catch (err) {
      console.error('Failed to load file:', err);
      setError('Could not load that file.');
    }
  };

  const handleDownload = async (file) => {
    try {
      const { blob, downloadUrl } = await apiService.downloadAccessSftpFile(file.id);

      // Large files come back as a short-lived presigned URL rather than bytes.
      if (downloadUrl) {
        window.open(downloadUrl, '_blank', 'noopener,noreferrer');
        return;
      }

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = file.filename || 'download';
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Download failed:', err);
      setError(err?.message || 'Could not download that file.');
    }
  };

  const handleReset = async (file) => {
    try {
      const updated = await apiService.resetAccessSftpFile(file.id);
      setSelectedFile((cur) => (cur && cur.id === file.id ? { ...cur, ...updated } : cur));
      setNotice(`${file.filename} will be retried on the next run.`);
    } catch (err) {
      console.error('Reset failed:', err);
      setError('Could not reset that file.');
    }
  };

  const handleRunNow = async () => {
    setSubmitting(true);
    try {
      const res = await apiService.runAccessSftpPullNow();
      setNotice(res?.detail || 'Pull started.');
      setError(null);
      setTimeout(refresh, 2500);
    } catch (err) {
      setError(err?.response?.data?.detail || 'Could not start a pull.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleToggleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const res = await apiService.toggleAccessSftpSchedule({
        confirm_phrase: confirmPhrase,
        enabled: status.enabled,
      });
      setNotice(res?.detail || 'Schedule updated.');
      setError(null);
      setConfirmOpen(false);
      setConfirmPhrase('');
      await refresh();
    } catch (err) {
      setError(err?.response?.data?.detail || 'Could not update the schedule.');
    } finally {
      setSubmitting(false);
    }
  };

  const expectedPhrase = status?.enabled ? status?.stop_phrase : status?.start_phrase;

  return (
    <div className="admin-dashboard">
      <StaffNavbar activeSection="superuser" />
      <div className="admin-main">
        <div className="admin-content">
          <BackButton to="/superuser/dashboard/" text="Back to Dashboard" />
          <h1 className="staff-page-header" style={{ margin: 0 }}>Access Pulls</h1>
          <p className="staff-page-subheader">
            Report files collected over SFTP from Access Development. Files are captured and
            stored as-is; nothing is parsed yet.
          </p>

          {error && <Banner kind="error">{error}</Banner>}
          {notice && <Banner kind="info">{notice}</Banner>}

          {loading && (
            <div style={{ fontFamily: '"Baikal Book"', fontSize: '14px', color: '#666' }}>
              Loading…
            </div>
          )}

          {!loading && status && (
            <>
              {/* --- schedule --- */}
              <div style={{
                ...cardStyle,
                borderLeft: `4px solid ${status.enabled ? '#10b981' : '#ef4444'}`,
              }}>
                <div style={{
                  display: 'flex', justifyContent: 'space-between',
                  alignItems: 'flex-start', gap: '16px', flexWrap: 'wrap',
                }}>
                  <div>
                    <h2 style={{
                      margin: 0, fontFamily: '"Baikal Book"', fontSize: '18px',
                    }}>
                      Schedule:{' '}
                      <span style={{ color: status.enabled ? '#10b981' : '#ef4444' }}>
                        {status.enabled ? 'RUNNING' : 'STOPPED'}
                      </span>
                    </h2>
                    <p style={{
                      margin: '8px 0 0', fontFamily: '"Baikal Book"',
                      fontSize: '13px', color: '#666',
                    }}>
                      {status.enabled
                        ? `Pulling every ${status.interval_minutes} minutes from `
                        : 'No scheduled pulls. Host '}
                      <code>{status.host}</code>
                      {status.remote_directory ? <> · directory <code>{status.remote_directory}</code></> : null}
                    </p>
                    <p style={{
                      margin: '6px 0 0', fontFamily: '"Baikal Book"',
                      fontSize: '13px', color: '#666',
                    }}>
                      Last run {formatDateTime(status.last_run_started_at)} ·
                      last success {formatDateTime(status.last_success_at)}
                    </p>
                    {status.enabled_changed_by && (
                      <p style={{
                        margin: '6px 0 0', fontFamily: '"Baikal Book"',
                        fontSize: '13px', color: '#666',
                      }}>
                        Last changed by {status.enabled_changed_by} at{' '}
                        {formatDateTime(status.enabled_changed_at)}
                      </p>
                    )}
                  </div>

                  <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                    <button
                      className="dashboard-button"
                      onClick={handleRunNow}
                      disabled={submitting}
                    >
                      Run once now
                    </button>
                    <button
                      className="dashboard-button"
                      onClick={() => { setConfirmOpen(true); setConfirmPhrase(''); }}
                      style={{ backgroundColor: status.enabled ? '#ef4444' : '#10b981', color: '#fff' }}
                    >
                      {status.enabled ? 'Stop hourly pull' : 'Start hourly pull'}
                    </button>
                    <button className="dashboard-button" onClick={refresh}>Refresh</button>
                  </div>
                </div>

                {status.enabled && !status.scheduler_env_enabled && (
                  <div style={{ marginTop: '16px' }}>
                    <Banner kind="warning">
                      <strong>Enabled here, but inert on this deployment.</strong>{' '}
                      <code>ACCESS_SFTP_SCHEDULER_ENABLED</code> is false, so no process is
                      running the schedule. Set it in the environment to activate.
                    </Banner>
                  </div>
                )}

                {confirmOpen && (
                  <form onSubmit={handleToggleSubmit} style={{
                    marginTop: '20px', padding: '16px',
                    border: `2px solid ${status.enabled ? '#ef4444' : '#10b981'}`,
                    borderRadius: '8px',
                  }}>
                    <p style={{ margin: '0 0 10px', fontFamily: '"Baikal Book"', fontSize: '14px' }}>
                      {status.enabled
                        ? 'This stops all scheduled pulls. Files that appear on the remote host will not be collected until this is turned back on, and the vendor may age them off their server in the meantime.'
                        : `This begins automatically connecting to ${status.host} every ${status.interval_minutes} minutes and downloading any new report files.`}
                    </p>
                    <p style={{ margin: '0 0 10px', fontFamily: '"Baikal Book"', fontSize: '14px' }}>
                      To confirm, type <code style={{ fontWeight: 700 }}>{expectedPhrase}</code> exactly:
                    </p>
                    <input
                      type="text"
                      value={confirmPhrase}
                      onChange={(e) => setConfirmPhrase(e.target.value)}
                      autoComplete="off"
                      autoCapitalize="off"
                      autoCorrect="off"
                      spellCheck="false"
                      style={{
                        width: '320px', padding: '10px 12px', border: '1px solid #dee2e6',
                        borderRadius: '8px', fontFamily: 'ui-monospace, monospace', fontSize: '14px',
                      }}
                    />
                    <div style={{ marginTop: '14px', display: 'flex', gap: '10px' }}>
                      <button
                        type="submit"
                        className="dashboard-button"
                        disabled={submitting || confirmPhrase !== expectedPhrase}
                        style={{
                          backgroundColor: status.enabled ? '#ef4444' : '#10b981',
                          color: '#fff',
                          opacity: confirmPhrase !== expectedPhrase ? 0.5 : 1,
                        }}
                      >
                        {status.enabled ? 'Stop hourly pull' : 'Start hourly pull'}
                      </button>
                      <button
                        type="button"
                        className="dashboard-button"
                        onClick={() => { setConfirmOpen(false); setConfirmPhrase(''); }}
                      >
                        Cancel
                      </button>
                    </div>
                  </form>
                )}
              </div>

              {/* --- runs --- */}
              <div style={cardStyle}>
                <h2 style={{ margin: '0 0 12px', fontFamily: '"Baikal Book"', fontSize: '18px' }}>
                  Recent pulls
                </h2>
                {runs.length === 0 ? (
                  <p style={{ fontFamily: '"Baikal Book"', fontSize: '14px', color: '#666' }}>
                    No pulls yet. Use <strong>Run once now</strong> to try a connection.
                  </p>
                ) : (
                  <div style={{ overflowX: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                      <thead>
                        <tr>
                          <th style={thStyle}>Started</th>
                          <th style={thStyle}>Trigger</th>
                          <th style={thStyle}>Status</th>
                          <th style={thStyle}>Duration</th>
                          <th style={thStyle}>Listed</th>
                          <th style={thStyle}>Downloaded</th>
                          <th style={thStyle}>Skipped</th>
                          <th style={thStyle}>Failed</th>
                          <th style={thStyle}>Size</th>
                          <th style={thStyle}></th>
                        </tr>
                      </thead>
                      <tbody>
                        {runs.map((run) => (
                          <tr key={run.id}>
                            <td style={tdStyle}>{formatDateTime(run.started_at)}</td>
                            <td style={tdStyle}>{run.trigger_display}</td>
                            <td style={tdStyle}>
                              <StatusPill status={run.status} label={run.status_display} />
                            </td>
                            <td style={tdStyle}>{formatDuration(run.duration_ms)}</td>
                            <td style={tdStyle}>{run.files_listed}</td>
                            <td style={tdStyle}>{run.files_downloaded}</td>
                            <td style={tdStyle}>{run.files_skipped}</td>
                            <td style={tdStyle}>{run.files_failed}</td>
                            <td style={tdStyle}>{formatBytes(run.bytes_downloaded)}</td>
                            <td style={tdStyle}>
                              <button
                                className="dashboard-button"
                                style={{ padding: '4px 12px', fontSize: '12px' }}
                                onClick={() => openRun(run.id)}
                              >
                                View
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>

              {/* --- run detail --- */}
              {selectedRun && (
                <div style={cardStyle}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <h2 style={{ margin: 0, fontFamily: '"Baikal Book"', fontSize: '18px' }}>
                      Pull on {formatDateTime(selectedRun.started_at)}
                    </h2>
                    <button className="dashboard-button" onClick={() => { setSelectedRun(null); setSelectedFile(null); }}>
                      Close
                    </button>
                  </div>

                  <p style={{ fontFamily: '"Baikal Book"', fontSize: '13px', color: '#666' }}>
                    {selectedRun.worker_id} · host key {selectedRun.host_key_fingerprint || '—'}
                  </p>

                  {selectedRun.error_message && (
                    <Banner kind="error">{selectedRun.error_message}</Banner>
                  )}

                  <h3 style={{ fontFamily: '"Baikal Book"', fontSize: '15px', marginTop: '20px' }}>
                    What the server listed
                  </h3>
                  {(selectedRun.listing_raw || []).length === 0 ? (
                    <p style={{ fontFamily: '"Baikal Book"', fontSize: '13px', color: '#666' }}>
                      Nothing listed.
                    </p>
                  ) : (
                    <div style={{ overflowX: 'auto' }}>
                      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                        <thead>
                          <tr>
                            <th style={thStyle}>Filename</th>
                            <th style={thStyle}>Size</th>
                            <th style={thStyle}>Modified</th>
                            <th style={thStyle}>Type</th>
                          </tr>
                        </thead>
                        <tbody>
                          {selectedRun.listing_raw.map((entry, i) => (
                            <tr key={`${entry.filename}-${i}`}>
                              <td style={{ ...tdStyle, fontFamily: 'ui-monospace, monospace' }}>
                                {entry.filename}
                              </td>
                              <td style={tdStyle}>{formatBytes(entry.size)}</td>
                              <td style={tdStyle}>
                                {entry.mtime_epoch
                                  ? new Date(entry.mtime_epoch * 1000).toLocaleString()
                                  : '—'}
                              </td>
                              <td style={tdStyle}>{entry.is_dir ? 'dir' : 'file'}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}

                  <h3 style={{ fontFamily: '"Baikal Book"', fontSize: '15px', marginTop: '24px' }}>
                    What happened to each file
                  </h3>
                  {(selectedRun.attempts || []).length === 0 ? (
                    <p style={{ fontFamily: '"Baikal Book"', fontSize: '13px', color: '#666' }}>
                      No file activity in this run.
                    </p>
                  ) : (
                    <div style={{ overflowX: 'auto' }}>
                      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                        <thead>
                          <tr>
                            <th style={thStyle}>Filename</th>
                            <th style={thStyle}>Outcome</th>
                            <th style={thStyle}>Reason</th>
                            <th style={thStyle}>Bytes</th>
                            <th style={thStyle}>Error</th>
                            <th style={thStyle}></th>
                          </tr>
                        </thead>
                        <tbody>
                          {selectedRun.attempts.map((a, i) => (
                            <tr key={`${a.file_id}-${i}`}>
                              <td style={{ ...tdStyle, fontFamily: 'ui-monospace, monospace' }}>
                                {a.filename}
                              </td>
                              <td style={tdStyle}>
                                <StatusPill status={a.outcome === 'downloaded' ? 'downloaded' : a.outcome} />
                              </td>
                              <td style={tdStyle}>{a.skip_reason || '—'}</td>
                              <td style={tdStyle}>{formatBytes(a.bytes_read)}</td>
                              <td style={{ ...tdStyle, color: '#991b1b', maxWidth: '320px' }}>
                                {a.error_message || '—'}
                              </td>
                              <td style={tdStyle}>
                                <button
                                  className="dashboard-button"
                                  style={{ padding: '4px 12px', fontSize: '12px' }}
                                  onClick={() => openFile(a.file_id)}
                                >
                                  Inspect
                                </button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              )}

              {/* --- file detail --- */}
              {selectedFile && (
                <div style={cardStyle}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
                    <h2 style={{ margin: 0, fontSize: '18px', fontFamily: 'ui-monospace, monospace' }}>
                      {selectedFile.filename}
                    </h2>
                    <div style={{ display: 'flex', gap: '10px' }}>
                      {selectedFile.has_content && (
                        <button className="dashboard-button" onClick={() => handleDownload(selectedFile)}>
                          Download full file
                        </button>
                      )}
                      {selectedFile.status === 'failed' && (
                        <button className="dashboard-button" onClick={() => handleReset(selectedFile)}>
                          Reset &amp; retry
                        </button>
                      )}
                      <button className="dashboard-button" onClick={() => setSelectedFile(null)}>Close</button>
                    </div>
                  </div>

                  <p style={{ fontFamily: '"Baikal Book"', fontSize: '13px', color: '#666' }}>
                    <StatusPill status={selectedFile.status} label={selectedFile.status_display} /> ·
                    {' '}{formatBytes(selectedFile.byte_size)} ·
                    {' '}revision <code>{selectedFile.revision_key}</code> ·
                    {' '}attempts {selectedFile.attempt_count} ·
                    {' '}first seen {formatDateTime(selectedFile.first_seen_at)}
                  </p>
                  <p style={{ fontFamily: '"Baikal Book"', fontSize: '12px', color: '#888', wordBreak: 'break-all' }}>
                    {selectedFile.remote_path} · sha256 {selectedFile.content_sha256 || '—'}
                  </p>

                  {selectedFile.is_duplicate && (
                    <Banner kind="warning">
                      Identical bytes to an earlier revision of this file — the vendor
                      re-uploaded it without changing the contents.
                    </Banner>
                  )}
                  {selectedFile.error_message && (
                    <Banner kind="error">{selectedFile.error_message}</Banner>
                  )}

                  {selectedFile.is_binary ? (
                    <p style={{ fontFamily: '"Baikal Book"', fontSize: '13px', color: '#666' }}>
                      Binary file — use the download button to inspect it.
                    </p>
                  ) : selectedFile.preview_text ? (
                    <>
                      {/* React escapes this; never render it as HTML. The vendor controls these bytes. */}
                      <pre style={{
                        maxHeight: '520px', overflow: 'auto', whiteSpace: 'pre',
                        fontFamily: 'ui-monospace, monospace', fontSize: '12px',
                        background: '#f8f8f8', padding: '12px',
                        border: '1px solid #ddd', borderRadius: '8px',
                      }}>
                        {selectedFile.preview_text}
                      </pre>
                      {selectedFile.preview_truncated && (
                        <p style={{ fontFamily: '"Baikal Book"', fontSize: '12px', color: '#888' }}>
                          Truncated preview — download the file for the full contents.
                        </p>
                      )}
                    </>
                  ) : (
                    <p style={{ fontFamily: '"Baikal Book"', fontSize: '13px', color: '#666' }}>
                      No preview stored.
                    </p>
                  )}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default SuperuserAccessPulls;
